import WrapperService from '@/modules/core/services/wrapper-service';
import {createPageableParams} from '@/modules/core/services/table-utils';

const API_URL = process.env.VUE_APP_ROOT_API + '/api/v1/display/advert';
const UPLOAD_URL = process.env.VUE_APP_ROOT_API + '/upload';
const API_STATIC_URL = process.env.VUE_APP_ROOT_DISPLAY_STATIC;

const sortTableFields = ["name", "active"];
const sortDatabaseFields = ["name", "active"];

const serviceName = "Advert-Service: ";

export default class AdvertService {

  static async readAllFromAPI(successF, errorF) {
    await WrapperService.get(API_URL, null, serviceName, successF, errorF);
  }

  static async readAllPaginatedFromAPI(page, size, search, sort, sortAsc, successF, errorF) {
    let params = createPageableParams(page, size, search, sort, sortAsc, successF, errorF, sortTableFields, sortDatabaseFields);
    await WrapperService.get(API_URL + '/page', {params: params}, serviceName, successF, errorF);
  }

  static async readFilterPaginatedFromAPI(page, size, search, sort, sortAsc, filter, successF, errorF) {
    let params = createPageableParams(page, size, search, sort, sortAsc, successF, errorF, sortTableFields, sortDatabaseFields);
    await WrapperService.post(API_URL + '/page/filter?' + params, filter, serviceName, successF, errorF);
  }

  static async readFromAPI(advertId, successF, errorF) {
    await WrapperService.get(API_URL + '/' + advertId, null, serviceName, successF, errorF);
  }

  static async removeOnAPI(advertId, successF, errorF) {
    await WrapperService.delete(API_URL + '/' + advertId, null, serviceName, successF, errorF);
  }

  static async downloadCsv(advertId, successF, errorF) {
    await WrapperService.get(API_URL + '/' + advertId + '/csv', null, serviceName, successF, errorF)
  }

  static async readAllRegions(successF, errorF) {
    await WrapperService.get(API_URL + '/regions', null, serviceName, successF, errorF);
  }

  static async readAllRegionsCount(successF, errorF) {
    await WrapperService.get(API_URL + '/regions/count', null, serviceName, successF, errorF);
  }

  static async postToAPI(advert, successF, errorF) {
    await WrapperService.post(API_URL, advert, serviceName, successF, errorF);
  }

  static async putToAPI(advert, successF, errorF) {
    await WrapperService.put(API_URL + '/' + advert.id, advert, serviceName, successF, errorF);
  }

  static async postToAPIWithFile(advert, file, successF, errorF) {

    let formData = new FormData();
    formData.append("uploadedFile", file, file.name);
    formData.append("advert", JSON.stringify(advert));

    await WrapperService.post(API_URL + '/withFile', formData, serviceName, successF, errorF);
  }

  static async putToAPIWithFile(advert, file, successF, errorF) {

    let formData = new FormData();
    formData.append("uploadedFile", file, file.name);
    formData.append("advert", JSON.stringify(advert));

    await WrapperService.put(API_URL + '/withFile/' + advert.id, formData, serviceName, successF, errorF);
  }

  static async getStatistic(advertId, successF, errorF) {
    await WrapperService.get(API_URL + '/' + advertId + '/statistic', null, serviceName, successF, errorF);
  }

  static async sendPdfGenerationRequest(advertId, successF, errorF) {
    await WrapperService.get(API_URL + '/' + advertId + '/pdf', null, serviceName, successF, errorF);
  }

  static async getStaticFileLink(id, onSuccess) {
    await WrapperService.get(API_URL + '/media/' + id + '/web', null, serviceName,
      (response) => {
        onSuccess(API_STATIC_URL + response.data)
      },
      () => {
        onSuccess(null)
      }
    )
  }

  static getThumbnailUrl(id){
    return API_STATIC_URL + '/advert/thumbnail/' + id + '?timestamp=' + Date.now();
  }

  static async getThumbnailData(id, onSuccess) {
    //timestamp is added to remove caching done by browsers
    await WrapperService.get(this.getThumbnailUrl(id), {responseType: 'blob'}, serviceName,
      response => {
        let reader = new window.FileReader()
        reader.readAsDataURL(response.data)
        reader.onload = () => {
          onSuccess(reader.result)
        }
      }
    )
  }

  static async getFileAsDataUrl(id, onlyLink, onSuccess) {
    await WrapperService.get(API_URL + '/media/' + id + '/web', null, serviceName,
      response => {
        let url = response.data;
        if (onlyLink) {
          onSuccess(API_STATIC_URL + url);
        } else {
          AdvertService.getMediaByUrl(url, onSuccess);
        }
      }
    )
  }

  static async getMediaByUrl(url, onSuccess) {
    await WrapperService.get(API_STATIC_URL + url, {responseType: 'blob'}, serviceName,
      response => {
        let reader = new window.FileReader()
        reader.readAsDataURL(response.data)
        reader.onload = () => {
          onSuccess(reader.result)
        }
      }
    )
  }

  static async getStatistics(paginator, filter, successF, errorF) {
    let params = createPageableParams(paginator.page,
      paginator.size,
      paginator.search,
      [paginator.sort],
      paginator.sortAsc,
      successF,
      errorF,
      sortTableFields,
      sortDatabaseFields);

    await WrapperService.post(API_URL + '/statistic?' + params, filter, serviceName, successF, errorF);
  }

  static async uploadMedia(file, successF, errorF) {
    let formData = new FormData();

    formData.append('media', file, file.name);

    await WrapperService.post(UPLOAD_URL + '/0', formData, serviceName, successF, errorF);
  }

  static async uploadCsvFile(file, successF, errorF) {
    let formData = new FormData();

    formData.append('csv', file, file.name);

    await WrapperService.put(UPLOAD_URL + '/advert/csv/convert/pdf', formData, serviceName, successF, errorF);
  }

  static updateFilterForSend(origFilter) {
    let filter = Object.assign({}, origFilter);

    filter.active = this.getBooleanOrNull(filter.active);
    filter.deleted = this.getBooleanOrNull(filter.deleted);
    filter.visibleAll = this.getBooleanOrNull(filter.visibleAll);
    filter.isPlayed = this.getBooleanOrNull(filter.isPlayed);
    filter.inHouse = this.getBooleanOrNull(filter.inHouse);

    return filter;
  }

  static getBooleanOrNull(value) {
    return (typeof value === 'undefined') || (value === 0) || (value === null) ? null : value === 1;
  }

  static async cloneById(id, successF, errorF) {
    await WrapperService.get(API_URL + '/clone/' + id, null, serviceName, successF, errorF);
  }
}
